/*
  The theme's own filters ("refine"): product options, product type and tags. The store only
  offers such filters through the Search & Discovery app, so the theme builds them itself.
  Every product in the list says what it is (data-refine, snippets/fable-refine-data.liquid);
  the groups in the panel (snippets/fable-facet-refine.liquid) are filled from the products
  that are there, and a choice hides the products that do not match it. The store never sees
  these choices: they travel in the address as refine.* parameters next to the store's own
  filter.* ones, so a link, a reload and the back button keep them, and a request to the
  store leaves them out.

  Within a group the choices widen (Moss or Sand); between groups they narrow (Moss and M).
  A product has a value when one of its variants has it; stock is not looked at.
*/
const FableRefine = {
  PREFIX: 'refine.',
  // sizes in wearing order; all other values follow them in alphabetical order
  SIZES: ['xxs', 'xs', 'xs/s', 's', 's/m', 'm', 'm/l', 'l', 'l/xl', 'xl', 'xxl', 'xxxl'],
  // the most pages of a list that are fetched to narrow it as a whole (see loadRest)
  MAX_PAGES: 20,

  enabled: false,
  // what the list on the page answers (see signature)
  shown: '',
  // the products of the list's other pages, and the list they belong to
  extra: [],
  extraKey: null,

  get root() {
    return document.querySelector('.fable-facets');
  },

  get grid() {
    return document.getElementById('product-grid');
  },

  get groups() {
    return Array.from(document.querySelectorAll('#FacetFiltersFormMobile .js-filter[data-refine-param]'));
  },

  // counts, chips and badges as the store rendered them (see capture)
  get synced() {
    return Array.from(document.querySelectorAll('[data-fable-facet-sync], #ProductCount'));
  },

  init() {
    this.extra = [];
    this.extraKey = null;
    this.enabled = this.groups.length > 0;
    if (!this.enabled) return;

    const searchParams = window.location.search.slice(1);
    const grid = this.grid;
    // a narrowed list is shown whole, from its first page
    if (grid && Number(grid.dataset.page) > 1 && this.active(this.split(searchParams).choices)) {
      const params = new URLSearchParams(searchParams);
      params.delete('page');
      window.location.replace(`${window.location.pathname}?${params}`);
      return;
    }

    this.shown = this.signature(searchParams);
    this.capture();
    this.apply(searchParams);
  },

  // The store has sent a new list (FacetFiltersForm.renderSection).
  afterRender() {
    if (!this.enabled) return;
    this.shown = this.signature(FacetFiltersForm.searchParamsPrev);
    this.extra = [];
    this.extraKey = null;
    this.capture();
    this.apply();
  },

  // A query string in two parts: what is asked of the store, and the theme's own choices.
  split(searchParams) {
    const server = new URLSearchParams();
    const choices = new Map();
    new URLSearchParams(searchParams || '').forEach((value, key) => {
      if (key.indexOf(this.PREFIX) !== 0) {
        server.append(key, value);
      } else if (value !== '') {
        if (!choices.has(key)) choices.set(key, new Set());
        choices.get(key).add(value);
      }
    });
    return { server: server.toString(), choices };
  },

  // What a query string asks of the store, in a form that tells whether two of them ask for the
  // same list: only what decides the list, the default order and the first page left unsaid.
  signature(searchParams) {
    const root = this.root;
    const sort = root ? root.dataset.refineSort : '';
    const asked = [];
    new URLSearchParams(searchParams || '').forEach((value, key) => {
      if (key === 'sort_by' && value === sort) return;
      if (key === 'page' && value === '1') return;
      if (key.indexOf('filter.') === 0 || key === 'sort_by' || key === 'page' || key === 'q') {
        asked.push(`${key}=${value}`);
      }
    });
    return asked.sort().join('&');
  },

  active(choices, groups = this.groups) {
    return groups.some((group) => (choices.get(group.dataset.refineParam) || new Set()).size > 0);
  },

  // the products of the list: those on the page and those of its other pages, shown or not
  items() {
    const grid = this.grid;
    const listed = grid ? Array.from(grid.querySelectorAll(':scope > li[data-refine]')) : [];
    return listed.concat(this.extra.filter((item) => item.parentNode !== grid));
  },

  data(item) {
    if (!item.refineData) {
      try {
        item.refineData = JSON.parse(item.dataset.refine);
      } catch (error) {
        item.refineData = {};
      }
    }
    return item.refineData;
  },

  // the tags a tag group offers (theme setting), lower case; none means every tag
  offered(group) {
    return (group.dataset.refineValues || '')
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);
  },

  // the values a product has in a group
  valuesOf(item, group) {
    const data = this.data(item);
    if (group.dataset.refineSource === 'option') {
      return ((data.options || {})[group.dataset.refineField] || []).map((entry) => entry[0]);
    }
    if (group.dataset.refineSource === 'type') return data.type ? [data.type] : [];
    if (group.dataset.refineSource === 'tag') {
      const offered = this.offered(group);
      return (data.tags || []).filter((tag) => !offered.length || offered.includes(tag.toLowerCase()));
    }
    return [];
  },

  // Does the product pass every group's choices? "except" leaves one group out: that is how a
  // value's count is taken (the products it would leave, whatever is chosen in its own group).
  matches(item, groups, choices, except) {
    return groups.every((group) => {
      const param = group.dataset.refineParam;
      const chosen = choices.get(param);
      if (param === except || !chosen || !chosen.size) return true;
      return this.valuesOf(item, group).some((value) => chosen.has(value));
    });
  },

  sort(group, values) {
    const offered = group.dataset.refineSource === 'tag' ? this.offered(group) : [];
    if (offered.length) {
      return values.sort((a, b) => offered.indexOf(a.toLowerCase()) - offered.indexOf(b.toLowerCase()));
    }
    const rank = (value) => {
      const index = this.SIZES.indexOf(value.toLowerCase());
      return index === -1 ? this.SIZES.length : index;
    };
    return values.sort(
      (a, b) => rank(a) - rank(b) || a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  },

  // a tag is written for the admin ("new-arrivals"); a visitor reads "New arrivals"
  label(group, value) {
    if (group.dataset.refineSource !== 'tag') return value;
    const words = value.replace(/[-_]+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
  },

  countText(count) {
    const root = this.root;
    const pattern = count === 1 ? root.dataset.refineCountOne : root.dataset.refineCountOther;
    return (pattern || '').replace(/\d+/, count);
  },

  clone(name) {
    const template = document.querySelector(`template[data-refine-template="${name}"]`);
    return template ? document.importNode(template.content.firstElementChild, true) : null;
  },

  // the round count badge of the Filter button and of a group's summary
  badge(node, count) {
    node.textContent = '';
    if (!count) return;
    const pill = document.createElement('span');
    pill.className = 'fable-pill__count';
    pill.setAttribute('aria-hidden', 'true');
    pill.textContent = count;
    const text = document.createElement('span');
    text.className = 'visually-hidden';
    text.textContent = (this.root.dataset.refineSelected || '').replace(/\d+/, count);
    node.append(pill, text);
  },

  // Counts, chips and badges come from the store's render, and a narrowed list rewrites them.
  // The store's version is kept, so that every pass starts from it.
  capture() {
    this.synced.forEach((node) => {
      node.refineBase = node.innerHTML;
    });
  },

  restore() {
    this.synced.forEach((node) => {
      if (typeof node.refineBase === 'string') node.innerHTML = node.refineBase;
    });
  },

  // Brings the page in line with the choices a query string names: the groups, the products,
  // the counts and the chips.
  apply(searchParams = FacetFiltersForm.searchParamsPrev) {
    const groups = this.groups;
    if (!groups.length) return;
    const { choices } = this.split(searchParams);
    const active = this.active(choices, groups);
    const items = this.items();

    groups.forEach((group) => this.renderGroup(group, groups, items, choices));

    let count = 0;
    items.forEach((item) => {
      const match = !active || this.matches(item, groups, choices);
      item.hidden = !match;
      if (match) count += 1;
    });

    this.renderList(active, count);
    FacetFiltersForm.keepStatusFocus(() => {
      this.restore();
      if (active) this.renderStatus(groups, choices, count, searchParams);
    });
    this.renderCards(groups, choices, items);

    // a group that has just been filled may start closed (renderGroup): not where groups are columns
    const drawer = document.querySelector('fable-facet-drawer');
    if (drawer && typeof drawer.syncGroups === 'function') drawer.syncGroups();
    if (active) this.loadRest();
  },

  // A group's rows: every value the products of the list have. A row is made once and then
  // only updated, so the one being ticked keeps the focus. A value that would leave no product
  // (given the other groups' choices) is disabled, as the store does with its own.
  renderGroup(group, groups, items, choices) {
    const param = group.dataset.refineParam;
    const chosen = choices.get(param) || new Set();
    const list = group.querySelector('[data-refine-list]');
    if (!list) return;

    const counts = new Map();
    const fills = new Map();
    items.forEach((item) => {
      const reachable = this.matches(item, groups, choices, param);
      this.valuesOf(item, group).forEach((value) => {
        counts.set(value, (counts.get(value) || 0) + (reachable ? 1 : 0));
      });
      if (group.dataset.refineSource === 'option') {
        ((this.data(item).options || {})[group.dataset.refineField] || []).forEach((entry) => {
          if (entry[1] && !fills.has(entry[0])) fills.set(entry[0], entry[1]);
        });
      }
    });
    // a choice no product here has (an old link, a list the store has narrowed since): it stays, to be undone
    chosen.forEach((value) => {
      if (!counts.has(value)) counts.set(value, 0);
    });

    const values = this.sort(group, Array.from(counts.keys()));
    const rows = new Map();
    Array.from(list.children).forEach((row) => {
      const input = row.querySelector('input');
      if (input && counts.has(input.value)) rows.set(input.value, row);
      else row.remove();
    });

    values.forEach((value, index) => {
      let row = rows.get(value);
      if (!row) {
        row = this.clone(group.dataset.refineKind);
        if (!row) return;
        const input = row.querySelector('input');
        input.name = param;
        input.value = value;
        row.querySelectorAll('[data-refine-text]').forEach((node) => {
          node.textContent = this.label(group, value);
        });
      }
      const input = row.querySelector('input');
      const count = counts.get(value);
      input.checked = chosen.has(value);
      input.disabled = count === 0 && !input.checked;
      row.querySelector('label').classList.toggle('fable-facet__option--disabled', input.disabled);
      const countNode = row.querySelector('[data-refine-count]');
      if (countNode) countNode.textContent = `(${this.countText(count)})`;
      const fill = row.querySelector('[data-refine-fill]');
      if (fill && fills.has(value)) fill.setAttribute('style', fills.get(value));
      if (list.children[index] !== row) list.insertBefore(row, list.children[index] || null);
    });

    group.hidden = values.length === 0;
    const badge = group.querySelector('[data-refine-badge]');
    if (badge) this.badge(badge, chosen.size);

    // in the sheet a long plain list starts closed, unless something in it is chosen
    if (values.length && !group.dataset.refineFilled) {
      group.dataset.refineFilled = 'true';
      if (group.dataset.refineKind === 'list' && values.length > 6 && !chosen.size) group.open = false;
    }
  },

  // A narrowed list is shown whole: the products of its other pages join it (loadRest) and the
  // page links go. When nothing is left, the "no products" message takes the list's place.
  renderList(active, count) {
    const grid = this.grid;
    // the store's own "no products" block has the grid's id when its list is empty
    if (!grid || grid.tagName !== 'UL') return;
    this.extra.forEach((item) => {
      if (active && item.parentNode !== grid) grid.appendChild(item);
      if (!active && item.parentNode === grid) item.remove();
    });
    const results = grid.parentElement;
    const pagination = results.querySelector('.pagination-wrapper');
    const empty = results.querySelector('[data-refine-empty]');
    if (pagination) pagination.hidden = active;
    grid.hidden = active && count === 0;
    if (empty) empty.hidden = !(active && count === 0);
  },

  // The count wherever it is shown, a chip for each choice, and the Filter button's badge.
  renderStatus(groups, choices, count, searchParams) {
    const root = this.root;
    const text = this.countText(count);
    root.querySelectorAll('.fable-facets__count, .fable-facet-drawer__count').forEach((node) => {
      node.textContent = text;
    });
    const apply = root.querySelector('[data-fable-facet-sync="apply"]');
    if (apply) apply.textContent = `${root.dataset.refineApply || ''} ${text}`.trim();
    const live = document.getElementById('ProductCount');
    if (live) live.textContent = text;

    const asked = new URLSearchParams(searchParams || '');
    const status = root.querySelector('.fable-facets__status');
    let list = status ? status.querySelector('.fable-facets__chips') : null;
    if (list) {
      // the store's own chips: taking one away must not take the theme's choices with it
      list.querySelectorAll('a.fable-facets__chip').forEach((link) => {
        const href = link.getAttribute('href');
        const index = href.indexOf('?');
        const params = new URLSearchParams(index === -1 ? '' : href.slice(index + 1));
        asked.forEach((value, key) => {
          if (key.indexOf(this.PREFIX) === 0) params.append(key, value);
        });
        link.setAttribute('href', `${index === -1 ? href : href.slice(0, index)}?${params}`);
      });
    } else if (status) {
      list = this.clone('chips');
      const clear = this.clone('clear');
      const panelClear = document.querySelector('.fable-facet-drawer__clear');
      if (clear && panelClear) clear.querySelector('a').setAttribute('href', panelClear.getAttribute('href'));
      if (list && clear) list.appendChild(clear);
      if (list) status.appendChild(list);
    }

    let chosenCount = 0;
    groups.forEach((group) => {
      const param = group.dataset.refineParam;
      const chosen = this.sort(group, Array.from(choices.get(param) || []));
      chosenCount += chosen.length;
      if (!list) return;
      chosen.forEach((value) => {
        const chip = this.clone('chip');
        if (!chip) return;
        const rest = new URLSearchParams();
        asked.forEach((otherValue, key) => {
          if (!(key === param && otherValue === value)) rest.append(key, otherValue);
        });
        chip.querySelector('a').setAttribute('href', `${window.location.pathname}?${rest}`);
        chip.querySelector('[data-refine-group]').textContent = group.dataset.refineLabel;
        chip.querySelector('[data-refine-text]').textContent = this.label(group, value);
        // before "clear all", which closes the list
        list.insertBefore(chip, list.lastElementChild);
      });
    });

    const badge = root.querySelector('[data-fable-facet-sync="badge"]');
    if (badge) {
      const pill = badge.querySelector('.fable-pill__count');
      this.badge(badge, (pill ? Number(pill.textContent) || 0 : 0) + chosenCount);
    }
  },

  // One colour chosen: each product card shows that colour (its photo, its link), as if its
  // swatch had been pressed. The card goes back to what it showed when the choice is undone,
  // unless the visitor has picked another colour on the card since.
  renderCards(groups, choices, items) {
    groups.forEach((group) => {
      if (group.dataset.refineKind !== 'swatch') return;
      const chosen = Array.from(choices.get(group.dataset.refineParam) || []);
      const wanted = chosen.length === 1 ? chosen[0] : null;
      const prefix = `${group.dataset.refineLabel}:`.toLowerCase();
      items.forEach((item) => {
        const card = item.querySelector('fable-card');
        if (!card || typeof card.selectSwatch !== 'function') return;
        const swatches = Array.from(card.querySelectorAll('.fable-card__swatch')).filter(
          (swatch) => (swatch.getAttribute('aria-label') || '').toLowerCase().indexOf(prefix) === 0
        );
        const pressed = swatches.find((swatch) => swatch.getAttribute('aria-pressed') === 'true');
        const target = wanted ? swatches.find((swatch) => swatch.dataset.value === wanted) : null;
        if (target) {
          if (target === pressed) return;
          if (!card.refineFrom) card.refineFrom = pressed || swatches[0];
          card.refineTo = target;
          card.selectSwatch(target);
        } else if (card.refineFrom) {
          if (pressed === card.refineTo && card.refineFrom !== pressed) card.selectSwatch(card.refineFrom);
          card.refineFrom = null;
          card.refineTo = null;
        }
      });
    });
  },

  // The products of the list's other pages (the same section, page by page), so that a list of
  // more than one page is narrowed as a whole, and its groups offer every value it has. They
  // wait here and join the list when it is narrowed (renderList). Fetched once per list.
  loadRest() {
    const grid = this.grid;
    if (!this.enabled || !grid || grid.tagName !== 'UL') return;
    const pages = Math.min(Number(grid.dataset.pages) || 1, this.MAX_PAGES);
    if (pages < 2 || this.extraKey === this.shown) return;
    const key = this.shown;
    this.extraKey = key;

    const current = Number(grid.dataset.page) || 1;
    const params = new URLSearchParams(this.split(FacetFiltersForm.searchParamsPrev).server);
    params.set('section_id', grid.dataset.id);
    const urls = [];
    for (let page = 1; page <= pages; page += 1) {
      if (page === current) continue;
      params.set('page', page);
      urls.push(`${window.location.pathname}?${params}`);
    }

    Promise.all(urls.map((url) => fetch(url).then((response) => (response.ok ? response.text() : ''))))
      .then((responses) => {
        // the store has sent another list meanwhile: these products are not its
        if (this.extraKey !== key || this.grid !== grid) return;
        const parser = new DOMParser();
        this.extra = [];
        responses.forEach((html) => {
          parser
            .parseFromString(html, 'text/html')
            .querySelectorAll('#product-grid > li[data-refine]')
            .forEach((item) => this.extra.push(document.importNode(item, true)));
        });
        this.apply();
      })
      .catch(() => {
        if (this.extraKey === key) this.extraKey = null;
      });
  },
};

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.submitTimer = null;

    const facetForm = this.querySelector('form');
    if (!facetForm) return;
    facetForm.addEventListener('input', this.onInput.bind(this));
  }

  // A change is sent 500ms after the last one. Until then this form holds a choice the server
  // has not seen, and no response may be rendered over it (see renderSection). A choice in one
  // of the theme's own groups needs no round trip (FableRefine): it goes at once, and takes
  // along whatever was still waiting.
  onInput(event) {
    FacetFiltersForm.pendingForms.add(this);
    clearTimeout(this.submitTimer);
    const name = event.target && event.target.name ? event.target.name : '';
    // the same goes for an order picked from the bar's list: one deliberate choice, nothing to wait for
    const picked = event.target && event.target.classList && event.target.classList.contains('fable-sort__select');
    const delay = picked || (FableRefine.enabled && name.indexOf(FableRefine.PREFIX) === 0) ? 0 : 500;
    this.submitTimer = setTimeout(() => {
      this.onSubmitHandler(event);
    }, delay);
  }

  // Back/forward and the "clear" links name a complete state of their own: a change that was
  // still waiting to be sent is dropped, so it can neither block that state nor undo it.
  // Returns whether there was one (its control then shows a choice that was never applied).
  static discardPendingInput() {
    const pending = FacetFiltersForm.pendingForms.size > 0;
    FacetFiltersForm.pendingForms.forEach((facetForm) => clearTimeout(facetForm.submitTimer));
    FacetFiltersForm.pendingForms.clear();
    return pending;
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      const dropped = FacetFiltersForm.discardPendingInput();
      FacetFiltersForm.renderPage(searchParams, null, false, dropped);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  // The status row (count, chips, clear all) is rewritten as a whole. When one of its links
  // holds the focus (the chip that was just used), the focus goes to the link that takes its
  // place, else to the last one, else to the Filter button.
  static keepStatusFocus(update) {
    const status = document.querySelector('.fable-facets__status');
    const focused = status && status.contains(document.activeElement) ? document.activeElement : null;
    const index = focused ? Array.from(status.querySelectorAll('a')).indexOf(focused) : -1;
    update();
    if (index === -1) return;
    const links = status.querySelectorAll('a');
    const next = links[Math.min(index, links.length - 1)] || document.querySelector('.fable-filterbar__trigger');
    if (next) next.focus({ preventScroll: true });
  }

  // "fresh" asks for the store's render even when the list itself would not change: a dropped
  // choice has left a control out of step (see discardPendingInput).
  static renderPage(searchParams, event, updateURLHash = true, fresh = false) {
    // the same state again (a choice made and taken back) is rendered, but is no new history entry
    if (searchParams === FacetFiltersForm.searchParamsPrev) updateURLHash = false;
    FacetFiltersForm.searchParamsPrev = searchParams;

    // Only the theme's own filters have changed: the list on the page is the one the store
    // would send again, so nothing is fetched.
    if (FableRefine.enabled && !fresh && FableRefine.signature(searchParams) === FableRefine.shown) {
      document.querySelectorAll('#ProductGridContainer .collection, #ProductCount').forEach((element) => {
        element.classList.remove('loading');
      });
      FacetFiltersForm.toggleActiveFacets(false);
      FableRefine.apply(searchParams);
      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
      return;
    }

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const gridContainer = document.getElementById('ProductGridContainer');
    const collection = gridContainer ? gridContainer.querySelector('.collection') : null;
    if (collection) collection.classList.add('loading');
    if (countContainer) countContainer.classList.add('loading');

    // the store is asked without the theme's own parameters: it would only ignore them
    const asked = FableRefine.enabled ? FableRefine.split(searchParams).server : searchParams;
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${asked}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, searchParams);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, searchParams) {
    // A request that is cut off because the visitor is leaving the page is not a failure.
    FacetFiltersForm.leaving = false;
    const onLeave = () => {
      FacetFiltersForm.leaving = true;
    };
    window.addEventListener('beforeunload', onLeave);
    window.addEventListener('pagehide', onLeave);
    const stopWatching = () => {
      window.removeEventListener('beforeunload', onLeave);
      window.removeEventListener('pagehide', onLeave);
    };

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        stopWatching();
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        // a slower, older request must not overwrite the state the visitor asked for last
        if (searchParams !== FacetFiltersForm.searchParamsPrev) return;
        FacetFiltersForm.renderSection(html, event);
      })
      .catch(() => {
        stopWatching();
        FacetFiltersForm.onRequestError(searchParams);
      });
  }

  // The address bar and the controls already show the requested state while the list still
  // shows the previous one. Load the page the address names: the visitor gets the requested
  // list from the server, or the browser's own error page, never a list that disagrees with
  // its filters. The list stays dimmed until that page arrives.
  static onRequestError(searchParams) {
    if (searchParams !== FacetFiltersForm.searchParamsPrev) return;
    if (FacetFiltersForm.leaving) {
      // cut off by a navigation that is already under way: just lift the pending state
      document.querySelectorAll('#ProductGridContainer .collection, #ProductCount').forEach((element) => {
        element.classList.remove('loading');
      });
      return;
    }
    FacetFiltersForm.leaving = true;
    window.location.assign(`${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`);
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event);
  }

  static renderSection(html, event) {
    // A newer choice is waiting to be sent (see onInput). This response does not know it:
    // rendering it would reset that control, and the request that follows would then be built
    // from the reset form. That request renders the newest state instead; the list stays dimmed.
    if (FacetFiltersForm.pendingForms.size) return;

    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
    FacetFiltersForm.renderResultsStatus(parsedHTML);
    // the theme's own filters are applied to the list the store has just sent
    FableRefine.afterRender();
  }

  static renderProductGridContainer(parsedHTML) {
    const source = parsedHTML.getElementById('ProductGridContainer');
    const target = document.getElementById('ProductGridContainer');
    if (!source || !target) return;
    target.innerHTML = source.innerHTML;
  }

  static renderProductCount(parsedHTML) {
    const source = parsedHTML.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    if (!container) return;
    if (source) container.innerHTML = source.innerHTML;
    container.classList.remove('loading');
  }

  // Search page: the visible result line (and the "nothing found" flag on the wrapper) sit in
  // the page header, outside the grid container. #ProductCount stays the announced live region.
  static renderResultsStatus(parsedHTML) {
    const header = document.querySelector('.fable-results__header');
    if (!header) return;
    const source = parsedHTML.querySelector('.fable-results__status');
    const target = header.querySelector('.fable-results__status');

    if (source && target) {
      target.innerHTML = source.innerHTML;
    } else if (source) {
      header.appendChild(document.importNode(source, true));
    } else if (target) {
      target.remove();
    }

    const results = header.closest('.fable-results');
    if (results) results.classList.toggle('template-search--empty', !source);
  }

  static renderFilters(parsedHTML, event) {
    const container = document.querySelector('#FacetFiltersFormMobile .fable-facet-drawer__groups');
    const sourceGroups = Array.from(parsedHTML.querySelectorAll('#FacetFiltersFormMobile .js-filter'));
    const activeFilter = event && event.target && event.target.closest ? event.target.closest('.js-filter') : null;

    if (container) {
      const liveGroup = (key) =>
        Array.from(container.querySelectorAll('.js-filter')).find((group) => group.dataset.index === key);

      // Groups are paired by their filter, not by their position: the store leaves out a filter
      // that has nothing to offer for the current results, and brings it back when it has.
      const keys = sourceGroups.map((source) => source.dataset.index);
      container.querySelectorAll('.js-filter').forEach((group) => {
        if (!keys.includes(group.dataset.index)) group.remove();
      });

      let previous = null;
      sourceGroups.forEach((source) => {
        let target = liveGroup(source.dataset.index);
        if (!target) {
          target = document.importNode(source, true);
          if (previous) {
            previous.after(target);
          } else {
            container.prepend(target);
          }
        } else if (target === activeFilter) {
          // The group being edited keeps its DOM (and the focus); only its summary is refreshed.
          FacetFiltersForm.renderCounts(source, target);
        } else if (!target.hasAttribute('data-refine-param')) {
          // (the theme's own groups are sent empty by the store: FableRefine keeps them filled)
          target.innerHTML = source.innerHTML;
        }
        previous = target;
      });
    }

    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(parsedHTML) {
    // the Filter button's badge, the status row, the panel's count and its main button's label
    FacetFiltersForm.keepStatusFocus(() => {
      document.querySelectorAll('[data-fable-facet-sync]').forEach((target) => {
        const source = parsedHTML.querySelector(`[data-fable-facet-sync="${target.dataset.fableFacetSync}"]`);
        if (source) target.innerHTML = source.innerHTML;
      });
    });

    // the panel's "clear" link keeps its node (it may hold the focus): only its target changes
    const sourceClear = parsedHTML.querySelector('.fable-facet-drawer__clear');
    const targetClear = document.querySelector('.fable-facet-drawer__clear');
    if (sourceClear && targetClear) targetClear.setAttribute('href', sourceClear.getAttribute('href'));

    // the sort control stays in the DOM (it may hold the focus): sync its value, list and label
    const sourceSelect = parsedHTML.querySelector('.sorting select');
    const targetSelect = document.querySelector('.sorting select');
    if (sourceSelect && targetSelect) {
      const selected = sourceSelect.querySelector('option[selected]');
      if (selected) {
        targetSelect.value = selected.value;
        // The list holds the usual orders plus the one in use. An order reached by the address
        // (or by going back) may be missing from the list on the page: take the response's list.
        if (targetSelect.value !== selected.value) {
          targetSelect.innerHTML = sourceSelect.innerHTML;
          targetSelect.value = selected.value;
          const sourceList = parsedHTML.querySelector('.fable-sort__list');
          const targetList = document.querySelector('.fable-sort__list');
          if (sourceList && targetList) targetList.innerHTML = sourceList.innerHTML;
        }
      }
      FacetFiltersForm.renderSortValue(targetSelect);
    }

    const drawer = document.querySelector('fable-facet-drawer');
    if (drawer && typeof drawer.syncTriggers === 'function') drawer.syncTriggers();
    if (drawer && typeof drawer.syncGroups === 'function') drawer.syncGroups();
  }

  // the order in use, on the bar's button and ticked in its list
  static renderSortValue(select) {
    const control = select.closest('.sorting');
    if (!control) return;
    const label = control.querySelector('.fable-sort__value');
    const option = select.options[select.selectedIndex];
    if (label && option) label.textContent = option.textContent.trim();
    control.querySelectorAll('.fable-sort__option').forEach((item) => {
      item.setAttribute('aria-selected', item.dataset.value === select.value ? 'true' : 'false');
    });
  }

  static renderCounts(source, target) {
    const sourceSummary = source.querySelector('summary');
    const targetSummary = target.querySelector('summary');
    if (sourceSummary && targetSummary) targetSummary.innerHTML = sourceSummary.innerHTML;
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    return grid ? [{ section: grid.dataset.id }] : [];
  }

  createSearchParams(form) {
    const params = new URLSearchParams(new FormData(form));
    // empty price fields would otherwise leave "filter.v.price.gte=" in the URL
    Array.from(params.keys()).forEach((key) => {
      if (key.indexOf('filter.') === 0 && params.getAll(key).every((value) => value === '')) params.delete(key);
    });
    return params;
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    FacetFiltersForm.pendingForms.delete(this);
    event.preventDefault();
    const form = event.target.closest('form');
    if (!form) return;
    const filtersForm = document.getElementById('FacetFiltersFormMobile');
    let params;

    if (form.id === 'FacetSortDrawerForm') {
      // The bar's sort select lives in its own form: start from the filter form so the
      // active filters are kept, then apply the chosen order.
      const select = form.querySelector('select[name="sort_by"]');
      params = this.createSearchParams(filtersForm || form);
      if (select) {
        params.set('sort_by', select.value);
        FacetFiltersForm.renderSortValue(select);
        if (filtersForm) {
          filtersForm.querySelectorAll('input[name="sort_by"]').forEach((input) => {
            input.checked = input.value === select.value;
          });
        }
      }
    } else {
      params = this.createSearchParams(form);
      const sortInput = form.querySelector('input[name="sort_by"]:checked');
      const select = document.querySelector('#FacetSortDrawerForm select[name="sort_by"]');
      if (sortInput && select && select.value !== sortInput.value) {
        select.value = sortInput.value;
        FacetFiltersForm.renderSortValue(select);
      }
    }

    this.onSubmitForm(params.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const dropped = FacetFiltersForm.discardPendingInput();
    FacetFiltersForm.toggleActiveFacets();
    const href = event.currentTarget.href;
    const params = new URLSearchParams(href.indexOf('?') == -1 ? '' : href.slice(href.indexOf('?') + 1));
    // a link that was rendered inside a section response must not carry that request's own parameter
    params.delete('section_id');
    FacetFiltersForm.renderPage(params.toString(), null, true, dropped);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.pendingForms = new Set();
FacetFiltersForm.leaving = false;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
  FacetFiltersForm.setListeners();
}

/*
  Price filter: the From and To fields are what the form sends. The quick ranges above them
  (radios that are not part of the form) only fill the two fields in; a range typed by hand
  marks the quick range it equals, or none.
*/
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.fields.forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('input', this.syncChoice.bind(this));
    });
    // runs before the form's own input listener, so the request is built from the filled fields
    this.addEventListener('input', this.onChoice.bind(this));
    this.setMinAndMaxValues();
  }

  get fields() {
    return Array.from(this.querySelectorAll('input[type="number"]'));
  }

  get choices() {
    return Array.from(this.querySelectorAll('input[type="radio"][data-min]'));
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.syncChoice();
  }

  onChoice(event) {
    const choice = event.target;
    if (!choice || !choice.matches || !choice.matches('input[type="radio"][data-min]')) return;
    const [minInput, maxInput] = this.fields;
    if (!minInput || !maxInput) return;
    minInput.value = choice.dataset.min;
    maxInput.value = choice.dataset.max;
    this.setMinAndMaxValues();
  }

  syncChoice() {
    const [minInput, maxInput] = this.fields;
    if (!minInput || !maxInput) return;
    // no lower bound and "from 0" are the same range; no upper bound only equals no upper bound
    const min = Number(minInput.value) || 0;
    this.choices.forEach((choice) => {
      const sameMin = (Number(choice.dataset.min) || 0) === min;
      const sameMax =
        choice.dataset.max === '' || maxInput.value === ''
          ? choice.dataset.max === maxInput.value
          : Number(choice.dataset.max) === Number(maxInput.value);
      choice.checked = sameMin && sameMax;
    });
  }

  setMinAndMaxValues() {
    const [minInput, maxInput] = this.fields;
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    if (input.value === '') return;
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      if (event.target.closest('a')) this.closeFilter(event);
    });
    this.addEventListener('keyup', (event) => {
      if (!event.target.closest('a')) return;
      event.preventDefault();
      if (event.code && event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
    this.setRole();
  }

  connectedCallback() {
    this.setRole();
  }

  setRole() {
    const facetLink = this.querySelector('a');
    if (facetLink) facetLink.setAttribute('role', 'button');
  }

  closeFilter(event) {
    const link = event.target.closest('a');
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    // without a filter form on the page the link simply navigates
    if (!link || !form || typeof form.onActiveFilterClick !== 'function') return;
    event.preventDefault();
    form.onActiveFilterClick({ preventDefault() {}, currentTarget: link });
  }
}

if (!customElements.get('facet-remove')) customElements.define('facet-remove', FacetRemove);

/*
  The sort control of the bar: a button that opens the list of orders in the theme's own look
  (a select's popup belongs to the operating system). The select in the form stays the value:
  a choice sets it and reports the change as the select itself would, so FacetFiltersForm
  needs to know nothing about the list.

  Keyboard: Enter, Space or an arrow key on the button opens the list on the order in use;
  in the list the arrows, Home and End move, Enter or Space choose, Escape or Tab close.
*/
class FableSortMenu extends HTMLElement {
  constructor() {
    super();
    this.onButtonClick = this.onButtonClick.bind(this);
    this.onButtonKeydown = this.onButtonKeydown.bind(this);
    this.onListKeydown = this.onListKeydown.bind(this);
    this.onListClick = this.onListClick.bind(this);
    this.onListPointer = this.onListPointer.bind(this);
    this.onOutsidePointer = this.onOutsidePointer.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.fable-sort__button');
    this.list = this.querySelector('.fable-sort__list');
    this.select = this.querySelector('select');
    if (!this.button || !this.list || !this.select) return;
    this.button.addEventListener('click', this.onButtonClick);
    this.button.addEventListener('keydown', this.onButtonKeydown);
    this.list.addEventListener('keydown', this.onListKeydown);
    this.list.addEventListener('click', this.onListClick);
    this.list.addEventListener('pointermove', this.onListPointer);
    document.addEventListener('pointerdown', this.onOutsidePointer);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onOutsidePointer);
  }

  get isOpen() {
    return Boolean(this.list) && !this.list.hidden;
  }

  // read each time: the list is rendered again when the store sends other orders
  get options() {
    return Array.from(this.list.querySelectorAll('.fable-sort__option'));
  }

  get active() {
    return this.list.querySelector('.fable-sort__option.is-active');
  }

  open() {
    if (this.isOpen) return;
    this.list.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    const options = this.options;
    this.setActive(options.find((option) => option.getAttribute('aria-selected') === 'true') || options[0]);
    this.list.focus({ preventScroll: true });
  }

  close(refocus) {
    if (!this.isOpen) return;
    this.list.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    this.setActive(null);
    if (refocus) this.button.focus({ preventScroll: true });
  }

  // the row the arrow keys or the pointer are on
  setActive(option) {
    this.options.forEach((item) => item.classList.toggle('is-active', item === option));
    if (option) {
      this.list.setAttribute('aria-activedescendant', option.id);
    } else {
      this.list.removeAttribute('aria-activedescendant');
    }
  }

  choose(option) {
    this.close(true);
    if (!option || this.select.value === option.dataset.value) return;
    this.select.value = option.dataset.value;
    // an order the select does not know (its list and this one are rendered together): no change
    if (this.select.value !== option.dataset.value) return;
    FacetFiltersForm.renderSortValue(this.select);
    this.select.dispatchEvent(new Event('input', { bubbles: true }));
  }

  onButtonClick() {
    if (this.isOpen) {
      this.close(true);
    } else {
      this.open();
    }
  }

  onButtonKeydown(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    this.open();
  }

  onListKeydown(event) {
    const options = this.options;
    const index = options.indexOf(this.active);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.setActive(options[Math.min(index + 1, options.length - 1)]);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setActive(options[Math.max(index - 1, 0)]);
        break;
      case 'Home':
        event.preventDefault();
        this.setActive(options[0]);
        break;
      case 'End':
        event.preventDefault();
        this.setActive(options[options.length - 1]);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.choose(this.active);
        break;
      case 'Escape':
        event.preventDefault();
        this.close(true);
        break;
      case 'Tab':
        // back to the button, from where the key moves on as usual
        this.close(true);
        break;
      default:
    }
  }

  onListClick(event) {
    const option = event.target.closest('.fable-sort__option');
    if (option) this.choose(option);
  }

  onListPointer(event) {
    const option = event.target.closest('.fable-sort__option');
    if (option && option !== this.active) this.setActive(option);
  }

  onOutsidePointer(event) {
    if (this.isOpen && !this.contains(event.target)) this.close(false);
  }
}

if (!customElements.get('fable-sort-menu')) customElements.define('fable-sort-menu', FableSortMenu);

/*
  Filter panel: a full-height sheet below 800px, a wide card from 800px (fable-collection.css).
  Opened by any [data-fable-filter-open] control that points at it with aria-controls (the
  Filter button in the bar); closed by [data-fable-filter-close] controls inside it, the scrim
  and Escape. Clicks are delegated from the document, and the trigger is looked up again by
  its key when focus is handed back.
*/
class FableFacetDrawer extends HTMLElement {
  constructor() {
    super();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.syncGroups = this.syncGroups.bind(this);
    this.triggerKey = null;
  }

  connectedCallback() {
    this.panel = this.querySelector('.fable-facet-drawer__panel');
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
    // the layout of the groups changes at this width (see syncGroups)
    this.wideQuery = window.matchMedia('(min-width: 800px)');
    if (this.wideQuery.addEventListener) this.wideQuery.addEventListener('change', this.syncGroups);
    this.watchResults();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    if (this.wideQuery && this.wideQuery.removeEventListener) {
      this.wideQuery.removeEventListener('change', this.syncGroups);
    }
    if (this.resultsObserver) this.resultsObserver.disconnect();
    this.resultsObserver = null;
    if (this.isOpen) document.body.classList.remove('overflow-hidden');
  }

  // The floating Filter / Sort pill (below 800px) belongs to the results. Once their end has
  // scrolled up past the bottom edge of the viewport it steps aside, so it does not sit on top
  // of the footer (or a banner that follows the grid). It stays over a hero above the results,
  // and on short lists that fit the screen, where it is the only way into the filters.
  watchResults() {
    const results = this.closest('.fable-plp');
    if (!results || typeof IntersectionObserver !== 'function') return;
    this.resultsObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        const rect = entry.boundingClientRect;
        const passed = !entry.isIntersecting && rect.top < 0 && rect.bottom <= window.innerHeight;
        results.classList.toggle('fable-plp--passed', passed);
      },
      // the root is reduced to a line along the bottom edge of the viewport
      { rootMargin: '-100% 0px 0px 0px' }
    );
    this.resultsObserver.observe(results);
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  get triggers() {
    return Array.from(document.querySelectorAll(`[data-fable-filter-open][aria-controls="${this.id}"]`));
  }

  get groups() {
    return Array.from(this.querySelectorAll('details.js-filter'));
  }

  isVisible(element) {
    // content of a closed group still has layout boxes in some browsers
    const scope = element.tagName === 'DETAILS' ? element.parentElement : element;
    const closedGroup = scope ? scope.closest('details:not([open])') : null;
    if (closedGroup && !(element.tagName === 'SUMMARY' && element.parentElement === closedGroup)) return false;
    if (typeof element.checkVisibility === 'function') return element.checkVisibility({ visibilityProperty: true });
    return element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
  }

  getFocusable() {
    const selector =
      'summary, a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])';
    return Array.from(this.querySelectorAll(selector)).filter((element) => this.isVisible(element));
  }

  onDocumentClick(event) {
    if (!event.target || !event.target.closest) return;
    const opener = event.target.closest('[data-fable-filter-open]');
    if (opener && opener.getAttribute('aria-controls') === this.id) {
      event.preventDefault();
      this.open(opener);
      return;
    }
    if (!this.isOpen) return;
    const closer = event.target.closest('[data-fable-filter-close]');
    if (closer && this.contains(closer)) {
      event.preventDefault();
      this.close();
    }
  }

  onDocumentKeydown(event) {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = this.getFocusable();
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    const inside = this.contains(active);

    if (event.shiftKey && (!inside || active === first || active === this.panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (!inside || active === last)) {
      event.preventDefault();
      first.focus();
    }
  }

  open(trigger) {
    this.triggerKey = trigger ? trigger.dataset.fableFilterOpen || '' : '';
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    this.syncTriggers();
    this.syncGroups();
    // a list of several pages: the theme's own groups then offer what all of its pages have
    FableRefine.loadRest();
    this.moveFocus(() => this.getFocusable()[0]);
  }

  // From 800px the groups are columns and their summaries are not displayed, so a group that is
  // closed there could not be opened: long lists start closed for the sheet, and a visitor may
  // have closed others before the window got wider. Chosen by layout (the summary's computed
  // display), which also holds while the panel itself is hidden.
  syncGroups() {
    this.groups.forEach((group) => {
      const summary = group.querySelector('summary');
      if (summary && getComputedStyle(summary).display === 'none') group.open = true;
    });
  }

  // Under prefers-reduced-motion the theme runs every property change as a 0.01ms transition,
  // so the drawer is still "hidden" (nothing in it is visible or focusable) for a frame or two
  // after it opens. The target is therefore looked up again on every attempt.
  moveFocus(resolveTarget, attempts = 12) {
    if (!this.isOpen || this.contains(document.activeElement)) return;
    const target = resolveTarget();
    if (target && target.isConnected) {
      target.focus({ preventScroll: true });
      if (document.activeElement === target) return;
    }
    if (attempts > 0) {
      requestAnimationFrame(() => this.moveFocus(resolveTarget, attempts - 1));
    } else if (this.panel) {
      // nothing took the focus: at least keep the keyboard inside the dialog
      this.panel.focus({ preventScroll: true });
    }
  }

  close() {
    if (!this.isOpen) return;
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    this.syncTriggers();

    const triggers = this.triggers.filter((trigger) => this.isVisible(trigger));
    const trigger = triggers.find((item) => (item.dataset.fableFilterOpen || '') === this.triggerKey) || triggers[0];
    if (trigger) trigger.focus({ preventScroll: true });
  }

  syncTriggers() {
    const open = this.isOpen;
    this.triggers.forEach((trigger) => {
      const isActive = open && (trigger.dataset.fableFilterOpen || '') === (this.triggerKey || '');
      trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('fable-facet-drawer')) customElements.define('fable-facet-drawer', FableFacetDrawer);

// The script is deferred: the list and the panel are there. In the theme editor the section
// comes back as new markup.
FableRefine.init();
document.addEventListener('shopify:section:load', () => FableRefine.init());
